<script setup lang="ts">
import { useSortByName } from '@/common/composables/sortByName';
import { useRefreshStorageAreaStore } from '@/storageAreas/stores/refreshStorageAreaStore';
import { storeToRefs } from 'pinia';
import { DataTable, Column, Button, InputText, Popover, Checkbox, MultiSelect, DatePicker, InputNumber, type DataTableFilterEvent  } from 'primevue';
import type { StorageArea } from 'shared/storageAreas/domain/storageArea';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useProductStore } from 'shared/products/stores/productStore';
import { useTranslations } from 'shared/translations/composables/useTranslations';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { getDaysSinceDate } from 'shared/common/date';
import { computed, onMounted, ref, watch } from 'vue';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useSelectedStorageAreaStore } from '../stores/selectedStorageAreaStore';
import { isBulkStorage, isLogYard, StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import { convertToDisplayUnit, convertToVolume, Unit } from 'shared/common/domain/bulkUnits';
import { StorageAreaStatus } from 'shared/storageAreas/domain/storageAreaStatus';
import { useFeatureFlagStore } from '@/featureFlags/stores/featureFlagStore';
import {
  IconSharpWarning,
  IconSharpKeyboardDoubleArrowUp,
  IconSharpKeyboardDoubleArrowDown,
  IconSharpClear
} from '@iconify-prerendered/vue-ic';
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import { useColumnSelector } from 'shared/common/composables/useColumnSelector';
import { formatNumber } from 'shared/common/domain/helperFunctions';
import { usePrimeVue } from 'primevue/config';

const primevue = usePrimeVue();
const storageAreaStore = useStorageAreaStore();
const translations = useTranslations();
const languageStore = useLanguageStore();
const productStore = useProductStore();
const featureFlags = useFeatureFlagStore();
const refreshStorageAreaStore = useRefreshStorageAreaStore();
const selectedStorageAreaStore = useSelectedStorageAreaStore();
const { loading } = storeToRefs(refreshStorageAreaStore);

const storageAreas = useSortByName({
  list: storeToRefs(storageAreaStore).storageAreas,
  getName: (s) => s.name,
});

function setDataTableLangage() {
  if (!primevue.config.locale) return;

  primevue.config.locale = translations.value.primeVueLocale;

}

onMounted(() => {
  setDataTableLangage();
  watch(() => languageStore.language.code, () => setDataTableLangage()
  )
});

interface StorageAreaTableRow extends StorageArea {
  productNames: string;
  productInfo: ProductInfo[];
  content: number | null;
  volume: number | null;
  firstMoveTo: Date | null;
  daysSinceFirstMove: number | null;
  allowedProducts: string;
  automaticProductChange: boolean | null;
  allowMultipleProducts: boolean | null;
}

const tableData = computed<StorageAreaTableRow[]>(() =>
  storageAreas.value.map((storageArea) => {
    const productData = getProductsFromStorageArea(storageArea);

    return {
      ...storageArea,
      productNames: productData.productNames,
      productInfo: productData.productInfo,

      content: getContent(storageArea),
      volume: getVolume(storageArea),
      firstMoveTo: getFirstMoveTo(storageArea),
      daysSinceFirstMove: getDaysSinceFirstMove(storageArea),
      allowedProducts: getAllowedProducts(storageArea),
      automaticProductChange: getAutomaticProductChange(storageArea),
      allowMultipleProducts: getAllowMultipleProducts(storageArea)

    } as StorageAreaTableRow;
  })
);

type ColumnKey =
  | 'name'
  | 'type'
  | 'status'
  | 'productNames'
  | 'content'
  | 'volume'
  | 'note'
  | 'displayText'
  | 'firstMoveTo'
  | 'daysSinceFirstMove'
  | 'allowedProducts'
  | 'automaticProductChange'
  | 'allowMultipleProducts'

type TableColumn = {
  field: string
  key: ColumnKey
  header?: string
  dataType?: string
  sortable?: boolean
  filter?: boolean
  filterField?: string
  showFilterMatchModes?: boolean
  showFilterOperator?: boolean
  showAddButton?: boolean
}

const baseColumns: TableColumn[] = [
  {
    field: 'name',
    key: 'name',
    sortable: true,
    filter: true,
    filterField: 'name',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'type',
    key: 'type',
    sortable: true,
    filter: true,
    filterField: 'type',
    showFilterMatchModes: false,
    showFilterOperator: false,
    showAddButton: false,
  },
  {
    field: 'status',
    key: 'status',
    sortable: true,
    filter: true,
    filterField: 'status',
    showFilterMatchModes: false,
    showFilterOperator: false,
  },
  {
    field: 'productNames',
    key: 'productNames',
    sortable: true,
    filter: true,
    filterField: 'productNames',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'content',
    key: 'content',
    dataType: 'numeric',
    sortable: true,
    filter: true,
    filterField: 'content',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'volume',
    key: 'volume',
    dataType: 'numeric',
    sortable: true,
    filter: true,
    filterField: 'volume',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'note',
    key: 'note',
    sortable: true,
    filter: true,
    filterField: 'note',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'displayText',
    key: 'displayText',
    sortable: true,
    filter: true,
    filterField: 'displayText',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'firstMoveTo',
    key: 'firstMoveTo',
    dataType: 'date',
    sortable: true,
    filter: true,
    filterField: 'firstMoveTo',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'daysSinceFirstMove',
    key: 'daysSinceFirstMove',
    dataType: 'numeric',
    sortable: true,
    filter: true,
    filterField: 'daysSinceFirstMove',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
    {
    field: 'allowedProducts',
    key: 'allowedProducts',
    sortable: true,
    filter: true,
    filterField: 'allowedProducts',
    showFilterMatchModes: true,
    showFilterOperator: true,
  },
  {
    field: 'automaticProductChange',
    key: 'automaticProductChange',
    sortable: true,
    filter: true,
    filterField: 'automaticProductChange',
    showFilterMatchModes: false,
    showFilterOperator: false,
  },
  {
    field: 'allowMultipleProducts',
    key: 'allowMultipleProducts',
    sortable: true,
    filter: true,
    filterField: 'allowMultipleProducts',
    showFilterMatchModes: false,
    showFilterOperator: false,
  },
]

const filters = ref();

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS, },
    name: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]},
    type: { value: null, matchMode: FilterMatchMode.IN },
    status: { value: null, matchMode: FilterMatchMode.IN },
    productNames: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]},
    note: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]},
    displayText: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]},
    content: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    volume: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    firstMoveTo: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    daysSinceFirstMove: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    allowedProducts: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]},
    automaticProductChange: { value: null, matchMode: FilterMatchMode.EQUALS },
    allowMultipleProducts: { value: null, matchMode: FilterMatchMode.EQUALS }
  }
}

initFilters();

function clearFilter() {
    initFilters();
};

const columns = computed<(TableColumn & { header: string })[]>(() => {
  const t = translations.value

  return baseColumns.map(col => {
    let header = ''

    switch (col.key) {
      case 'name': header = t.generic.name; break
      case 'type': header = t.generic.type; break
      case 'status': header = t.generic.status; break
      case 'productNames': header = t.generic.products; break
      case 'content': header = `${t.generic.content} (${t.generic.inTotal})`; break
      case 'volume': header = `${t.generic.volume} (m3)`; break
      case 'note': header = t.generic.note; break
      case 'displayText': header = t.systemSettings.page.storagearea.displayText; break
      case 'firstMoveTo': header = t.storageAreas.storageAreaTable.firstMoveTo; break
      case 'daysSinceFirstMove': header = t.storageAreas.storageAreaTable.daysSinceFirstMove; break
      case 'allowedProducts': header = t.dialogs.storageArea.allowedProducts; break
      case 'automaticProductChange': header = t.dialogs.storageArea.automaticProductChange; break
      case 'allowMultipleProducts': header = t.dialogs.storageArea.allowMultipleProducts; break
    }

    return { ...col, header }
  })
})

const defaultVisibleFields = ['name', 'type', 'status', 'productNames', 'content', 'volume', 'note', 'displayText', 'firstMoveTo', 'daysSinceFirstMove'];

const {
  orderedColumns,
  selectedColumns: visibleColumns,
  setSelectedColumns,
  setOrder,
  reset,
} = useColumnSelector<TableColumn & { header: string }>({
  storageKey: 'map-table-columns',
  columns,
  defaultFields: defaultVisibleFields,
})

//popover för produkter
const popover = ref();
const selectedStorageArea = ref<StorageAreaTableRow | null>(null);

function togglePopover(event: Event, storageArea: StorageAreaTableRow) {
  selectedStorageArea.value = storageArea;
  popover.value.toggle(event);
}

const emit = defineEmits<{
  (e: 'flyToStorageArea', storageArea: StorageArea): void
  (e: 'fullscreen'): void
  (e: 'minimize'): void
}>()

// function editStorageArea(storageArea: StorageArea) {
//     emit('editStorageArea', storageArea)
// }

function flyToStorageArea(storageArea: StorageArea) {
  emit('flyToStorageArea', storageArea)
}

const statusOptions = ref(
  Object.values(StorageAreaStatus)
    .filter((v) => typeof v === 'number')
    .map((value) => ({
      label: translations.value.enums.storageAreaStatus(value),
      value,
    }))
);

const typeOptions = computed(() => {

  const baseTypes: StorageAreaType[] = [
    // StorageAreaType.Producer,
    // StorageAreaType.Consumer,
    // StorageAreaType.Truck,
    // StorageAreaType.Train,
  ];

  const values = [...baseTypes];

  if (featureFlags.isLogYardEnabled()) {
    values.push(StorageAreaType.LogSorterIntake);
    values.push(StorageAreaType.SawLineIntake);
    values.push(StorageAreaType.LogSorterBin);
    values.push(StorageAreaType.SortedPile);
    values.push(StorageAreaType.UnsortedPile);
  }

  if (featureFlags.isCoilEnabled()) {
    values.push(StorageAreaType.CoilStack);
  }

  if (featureFlags.isBulkEnabled()) {
    values.push(StorageAreaType.Bulk);
  }

  return values
    .map((value) => ({
      label: translations.value.enums.storageAreaType(value),
      value,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
});


// Hämtar alla produkter som finns kopplade lagringsområde och returnerar ett objekt med array och lista
function getProductsFromStorageArea(storageArea: StorageArea): {
  productInfo: ProductInfo[];
  productNames: string;
} {
  let productInfo: ProductInfo[] = [];

  switch (true) {
    case isLogYard(storageArea.type): {
      if (!storageArea.logYardData) {
        return { productInfo: [], productNames: '' };
      }

      const sortedContent = [...storageArea.logYardData.content].sort(
        (a, b) => b.numberOfLogs - a.numberOfLogs
      );

      for (const content of sortedContent) {
        const product = productStore.getById(content.productId);

        if (product) {
          productInfo.push({
            id: product.id,
            productName: product.name,
            content: content.numberOfLogs ? formatNumber(content.numberOfLogs) + ' ' + translations.value.logYard.logs : '',
            volume: formatNumber(content.volume),
          });
        }
      }

      break;
    }

    case isBulkStorage(storageArea.type): {
      if (!storageArea.bulkStorageData) {
        return { productInfo: [], productNames: '' };
      }

      const sortedContent = [...storageArea.bulkStorageData.content].sort(
        (a, b) => b.ton - a.ton
      );

      for (const content of sortedContent) {
        const product = productStore.getById(content.productId);

        if (product) {
          if (!product.bulkData) continue;

          const density = productStore.getBulkDensity(content.productId);

          const volume = convertToVolume(
            content.ton,
            Unit.MetricTon,
            density
          );

          productInfo.push({
            id: product.id,
            productName: product.name,
            content: content.ton ? formatNumber(convertToDisplayUnit(content.ton, product.bulkData.unit, density)) + ' ' + translations.value.bulk.bulkUnitShort(product.bulkData.unit) : '',
            volume: formatNumber(volume),
          });
        }
      }

      break;
    }

    default:
      return { productInfo: [], productNames: '' };
  }

  const productList = productInfo.map((p) => p.productName).join(', ');

  return {
    productInfo,
    productNames: productList,
  };
}

const filteredCount = ref(0);

function onFilter(event: DataTableFilterEvent) {
  filteredCount.value = event.filteredValue?.length ?? tableData.value.length;
}

function getAllowedProducts(storageArea: StorageArea): string {
  let productIds: string[] | undefined;

  if (isLogYard(storageArea.type)) {
    productIds = storageArea.logYardData?.allowedProducts;
  } else if (isBulkStorage(storageArea.type)) {
    productIds = storageArea.bulkStorageData?.allowedProducts;
  }

  if (!productIds?.length) {
    return '';
  }

  return productIds
    .map(id => productStore.getById(id)?.name)
    .filter((name): name is string => !!name)
    .join(', ');
}

function getContent(storageArea: StorageArea): number | null {
  switch (true) {
    case isLogYard(storageArea.type): {
      const count =
        storageArea.logYardData?.content?.reduce(
          (sum, item) => sum + item.numberOfLogs,
          0
        ) ?? 0;

      return count === 0 ? null : count;
    }

    case isBulkStorage(storageArea.type): {
      const tons =
        storageArea.bulkStorageData?.content?.reduce(
          (sum, item) => sum + item.ton,
          0
        ) ?? 0;

      return tons === 0 ? null : tons;
    }

    default:
      return null;
  }
}

function getVolume(storageArea: StorageArea): number | null {
  switch (true) {
    case isLogYard(storageArea.type): {
      const volume =
        storageArea.logYardData?.content?.reduce(
          (sum, item) => sum + item.volume,
          0
        ) ?? 0;

      return volume === 0 ? null : volume;
    }

    case isBulkStorage(storageArea.type): {
      const volume =
        storageArea.bulkStorageData?.content?.reduce((sum, item) => {
          const density = productStore.getBulkDensity(item.productId);
          return sum + convertToVolume(item.ton, Unit.MetricTon, density);
        }, 0) ?? 0;

      return volume === 0 ? null : volume;
    }

    default:
      return null;
  }
}

function getFirstMoveTo(storageArea: StorageArea): Date | null {
  switch (true) {
    case isLogYard(storageArea.type):
      return storageArea.logYardData?.firstMoveToUtc
        ? new Date(storageArea.logYardData.firstMoveToUtc)
        : null;

    case isBulkStorage(storageArea.type):
      return storageArea.bulkStorageData?.firstMoveToUtc
        ? new Date(storageArea.bulkStorageData.firstMoveToUtc)
        : null;

    default:
      return null;
  }
}

function getDaysSinceFirstMove(storageArea: StorageArea) {
  switch (true) {
    case isLogYard(storageArea.type):
      return storageArea.logYardData?.firstMoveToUtc
        ? Number(getDaysSinceDate(storageArea.logYardData.firstMoveToUtc))
        : null;

    case isBulkStorage(storageArea.type):
      return storageArea.bulkStorageData?.firstMoveToUtc
        ? getDaysSinceDate(storageArea.bulkStorageData.firstMoveToUtc)
        : null;

    default:
      return null;
  }
}

function getAutomaticProductChange(storageArea: StorageArea): boolean | null {
  switch (true) {
    case isLogYard(storageArea.type): {
      const disabled =
        storageArea.logYardData?.disableAutomaticProductChange;

      return disabled == null ? null : !disabled;
    }

    case isBulkStorage(storageArea.type): {
      const disabled =
        storageArea.bulkStorageData?.disableAutomaticProductChange;

      return disabled == null ? null : !disabled;
    }

    default:
      return null;
  }
}

function getAllowMultipleProducts(storageArea: StorageArea): boolean | null {
  switch (true) {
    case isLogYard(storageArea.type):
      return storageArea.logYardData?.allowMultipleProducts ?? null;

    case isBulkStorage(storageArea.type):
      return storageArea.bulkStorageData?.allowMultipleProducts ?? null;

    default:
      return null;
  }
}

//beskriver strukturen för ProduktInfo-objektet
interface ProductInfo {
  id: string;
  productName: string;
  content: string;
  volume: string;
}

const selectedProducts = ref();

watch(
  [
    () => selectedStorageAreaStore.selectedStorageAreaIds,
    storageAreas
  ],
  ([selectedIds = []]) => {
    selectedProducts.value = storageAreas.value.filter(storageArea =>
      selectedIds.includes(storageArea.id)
    );
  },
  { immediate: true }
);

</script>

<template>
  <DataTable class="storage-area-table"
  key="key"
  :value="tableData"
  data-key="id"
  sort-field="name"
  scrollable
  :loading="loading"
  scrollHeight="flex"
  :virtualScrollerOptions="{ itemSize: 46 }"
  width="auto"
  v-model:filters="filters"
  @filter="onFilter"
  size="small"
  filterDisplay="menu"
  v-model:selection="selectedProducts"

  >
    <template #header>
      <div class="table-header-grid">
        <span class="text-xl font-bold">{{ translations.generic.storageAreas }}
          {{ (filteredCount < tableData.length) ? ('(' + translations.dataTable.showing + ' ' + filteredCount + ' ' +
            translations.dataTable.of + ' ' + tableData.length + ')') : "" }}</span>

            <Button v-if="filteredCount < tableData.length" type="button" variant="outlined" severity="secondary"
              size="small" @click="clearFilter">
              <IconSharpClear />
              {{ translations.dataTable.clearFilters }}
            </Button>

            <InputText class="search-field" autofocus v-model="filters.global.value"
              :placeholder="translations.generic.search" />

            <ColumnSelectorButton class="column-selector-button" :columns="orderedColumns" :modelValue="visibleColumns" variant="outlined" size="small"
              @update:modelValue="setSelectedColumns" @reorder="setOrder" @reset="reset" />
            <Button class="minimize-button" type="button" variant="outlined" severity="secondary" size="small"
              @click="() => emit('minimize')">
              <IconSharpKeyboardDoubleArrowDown />
            </Button>
            <Button class="fullscreen-button" type="button" variant="outlined" severity="secondary" size="small"
              @click="() => emit('fullscreen')">
              <IconSharpKeyboardDoubleArrowUp />
            </Button>
      </div>
    </template>
    <template #empty>{{ translations.storageAreas.notFound }}</template>
    <template #loading>{{ translations.generic.loadingTableData }}</template>
    <Column class="w-1" key="flyToStorageArea">
      <template #body="{ data }">
        <div class="flex gap-2">
          <Button icon="pi pi-search" @click="flyToStorageArea(data)" severity="secondary" rounded></Button>
        </div>
      </template>
    </Column>

    <Column v-for="col in visibleColumns" :key="col.field" :field="col.field" :header="col.header"
      :sortable="col.sortable" :filter="col.filter" :filterField="col.filterField"
      :showFilterMatchModes="col.showFilterMatchModes" :showFilterOperator="col.showFilterOperator"
      :showAddButton="col.showAddButton" :dataType="col.dataType"
      >

      <template #body="{ data, column }">
        <template v-if="col.field === 'type'">
          {{ translations.enums.storageAreaType(data.type) }}
        </template>

        <template v-else-if="col.field === 'status'">
          {{ translations.enums.storageAreaStatus(data.status) }}
        </template>

        <template v-else-if="col.field === 'productNames'">
          <div class="flex items-center gap-2 w-full">
            <Button v-if="data.productNames" icon="pi pi-info-circle" rounded text class="shrink-0"
              @click="togglePopover($event, data)" />

            <p class="truncate flex-1">
              {{ data.productNames }}
            </p>
          </div>
        </template>

        <template v-else-if="col.field === 'content'">
          {{
            data.content != null ? (formatNumber(data.content) + ' ' +
              (isLogYard(data.type) ? translations.logYard.logs
                : isBulkStorage(data.type)
                  ? translations.bulk.bulkUnitShort(Unit.MetricTon)
                  : ''
              )
            )
              : ''
          }}
        </template>

        <template v-else-if="col.field === 'volume'">
          {{ formatNumber(data.volume) }}
        </template>

        <template v-else-if="col.field === 'note'">
          <div class="flex items-center gap-2 w-full">
            <IconSharpWarning v-if="data.importantNote" class="w-6 h-6 text-amber-500" />
            {{ data.note }}
          </div>
        </template>

        <template v-else-if="col.field === 'firstMoveTo'">
          {{ languageStore.formatDate(data.firstMoveTo) }}
        </template>

        <template v-else-if="col.field === 'automaticProductChange'">
          {{
            data.automaticProductChange == null
              ? ''
              : data.automaticProductChange
                ? translations.generic.yes
                : translations.generic.no
          }}
        </template>

        <template v-else-if="col.field === 'allowMultipleProducts'">
          {{
            data.allowMultipleProducts == null
              ? ''
              : data.allowMultipleProducts
                ? translations.generic.yes
                : translations.generic.no
          }}
        </template>

        <template v-else>
          {{ data[col.field] }}
        </template>
      </template>

      <template v-if="col.filter" #filter="{ filterModel, filterCallback }">
        <template v-if="col.field === 'name'">
          <InputText v-model="filterModel.value" size="small" type="text" @input="filterCallback()"
            :placeholder="translations.dataTable.filterBy + ' ' + translations.generic.name.toLocaleLowerCase()" fluid />
        </template>

        <template v-else-if="col.field === 'type'">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="typeOptions" optionLabel="label"
            optionValue="value" :placeholder="translations.generic.type" size="small" style="min-width: 12rem" appendTo="self" />
        </template>

        <template v-else-if="col.field === 'status'">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="statusOptions"
            optionLabel="label" optionValue="value" :placeholder="translations.generic.status" size="small"
            style="min-width: 12rem" appendTo="self" />
        </template>

        <template v-else-if="col.field === 'productNames'">
          <InputText v-model="filterModel.value" size="small" type="text" @input="filterCallback()"
            :placeholder="translations.dataTable.filterBy + ' ' + translations.generic.products.toLocaleLowerCase()" fluid />
        </template>

        <template v-else-if="col.field === 'content'">
          <InputNumber v-model="filterModel.value" mode="decimal" />
        </template>

        <template v-else-if="col.field === 'volume'">
          <InputNumber v-model="filterModel.value" mode="decimal" />
        </template>

        <template v-else-if="col.field === 'note'">
          <InputText v-model="filterModel.value" size="small" type="text" @input="filterCallback()"
            :placeholder="translations.dataTable.filterBy + ' ' + translations.generic.note.toLocaleLowerCase()"
            fluid />
        </template>

        <template v-else-if="col.field === 'displayText'">
          <InputText v-model="filterModel.value" size="small" type="text" @input="filterCallback()"
            :placeholder="translations.dataTable.filterBy + ' ' + translations.systemSettings.page.storagearea.displayText.toLocaleLowerCase()"
            fluid />
        </template>

        <template v-else-if="col.field === 'firstMoveTo'">
          <DatePicker v-model="filterModel.value" dateFormat="yy-mm-dd" placeholder="yy-mm-dd" appendTo="self" />
        </template>

        <template v-else-if="col.field === 'daysSinceFirstMove'">
          <InputNumber v-model="filterModel.value" mode="decimal" />
        </template>

        <template v-else-if="col.field === 'allowedProducts'">
          <InputText v-model="filterModel.value" size="small" type="text" @input="filterCallback()"
            :placeholder="translations.dataTable.filterBy + ' ' + translations.generic.products.toLocaleLowerCase()"
            fluid />
        </template>

        <template v-else-if="col.field === 'automaticProductChange'">
          <label for="automaticProductChange-filter" class="font-bold">
            {{ translations.dialogs.storageArea.automaticProductChange }} </label>
          <Checkbox v-model="filterModel.value" :indeterminate="filterModel.value === null" binary
            inputId="automaticProductChange-filter" />
        </template>

        <template v-else-if="col.field === 'allowMultipleProducts'">
          <label for="allowMultipleProducts-filter" class="font-bold"> {{
            translations.dialogs.storageArea.allowMultipleProducts }} </label>
          <Checkbox v-model="filterModel.value" :indeterminate="filterModel.value === null" binary
            inputId="allowMultipleProducts-filter" />
        </template>

      </template>
    </Column>
  </DataTable>
  <Popover ref="popover">
    <DataTable :value="selectedStorageArea?.productInfo ?? []">
      <Column field="productName" :header="translations.generic.products"></Column>
      <Column field="content" :header="translations.generic.content"></Column>
      <Column field="volume" :header="translations.generic.volume + (' (m3)')"></Column>
    </DataTable>
  </Popover>
</template>

<style scoped>
.product-card {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  border: 1px solid var(--p-surface-300);
  min-width: 8rem;
}

.storage-area-container {
  height: 100%;
  background-color: var(--p-surface-100);
  overflow-y: auto;
}

.table-header-grid {
  display: grid;
  grid-template-columns: 1fr auto auto auto auto auto;
  align-items: center;
  gap: 0.5rem;
}

.clear-filter-button {
  grid-column: 2;
}

.search-field {
  grid-column: 3;
  max-width: 16em;
}

.column-select {
  grid-column: 4;
  font-size: small;
  font-weight: inherit;
}

.minimize-button {
  grid-column: 5;
}

.fullscreen-button {
  grid-column: 6;
}

.table-header-grid .column-selector-button,
.table-header-grid .minimize-button,
.table-header-grid .fullscreen-button {
  width: 2.0rem;
  height: 2.0rem;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex-shrink: 0;
}

.table-header-grid .column-selector-button .p-button-icon,
.table-header-grid .minimize-button svg,
.table-header-grid .fullscreen-button svg {
  width: 1rem;
  height: 1rem;
  font-size: 1rem;
}
</style>
